import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

applyPlugin(jsPDF);

/**
 * Obtiene el valor formateado de una celda para el PDF.
 * Reutiliza la lógica de getFormattedValueForCSV de DataExplorerPage.
 * @param {*} cellValue - Valor de la celda (puede ser objeto con comparison data)
 * @param {boolean} isComparison - Si estamos en modo comparación
 * @returns {string|{mainLine: string, deltaLine: string}} - String simple o objeto con líneas
 */
function getFormattedCellValue(cellValue, isComparison) {
    if (cellValue === null || cellValue === undefined) return '';

    // Valor con datos de comparación
    if (typeof cellValue === 'object' && cellValue.formattedCurrentValue !== undefined) {
        const mainLine = cellValue.formattedCurrentValue !== null
            ? cellValue.formattedCurrentValue
            : '';
        if (isComparison && cellValue.formattedDelta) {
            return { mainLine, deltaLine: cellValue.formattedDelta };
        }
        return mainLine;
    }

    // Valor formateado simple (dimensiones y métricas sin comparación)
    if (typeof cellValue === 'object' && cellValue.formattedValue !== undefined) {
        return cellValue.formattedValue !== null ? cellValue.formattedValue : '';
    }

    return String(cellValue);
}

/**
 * Construye la fila de resumen (totales) para la tabla.
 * Reutiliza la lógica de getSummaryValueForColumn de DataExplorerTable.
 * @param {Array} headers - Headers de la tabla [{key, label}, ...]
 * @param {Object} summary - Objeto con valores de resumen por fieldId
 * @param {boolean} isComparison - Si estamos en modo comparación
 * @returns {Object|null} - Fila de resumen o null si no hay summary
 */
function buildSummaryRow(headers, summary, isComparison) {
    if (!summary || Object.keys(summary).length === 0) return null;

    const row = {};
    let isFirstDimension = true;

    for (const header of headers) {
        const key = header.key;

        if (!key.startsWith('metric_')) {
            // Columna de dimensión
            row[key] = isFirstDimension ? 'Total' : '';
            isFirstDimension = false;
            continue;
        }

        // Columna de métrica: buscar en summary
        const fieldId = key.replace(/^metric_/, '');
        let summaryValue = summary[fieldId];

        // Fallback case-insensitive
        if (!summaryValue) {
            const matchingKey = Object.keys(summary).find(
                k => k.toLowerCase() === fieldId.toLowerCase()
            );
            if (matchingKey) summaryValue = summary[matchingKey];
        }

        if (summaryValue) {
            row[key] = getFormattedCellValue(summaryValue, isComparison);
        } else {
            row[key] = '';
        }
    }

    return row;
}

/**
 * Construye estilos de alineación por columna para autoTable.
 * Métricas alineadas a la derecha, dimensiones a la izquierda.
 * @param {Array} headers - Headers de la tabla
 * @returns {Object} - columnStyles para jspdf-autotable
 */
function buildColumnStyles(headers) {
    const styles = {};
    headers.forEach((h, i) => {
        styles[i] = {
            halign: h.key.startsWith('metric_') ? 'right' : 'left',
        };
    });
    return styles;
}

/**
 * Exporta el reporte del Explorador de Datos como PDF.
 * Charts se renderizan como JPEG comprimido, la tabla como texto nativo.
 * @param {Object} refs - { lineChartRef, chartsSectionRef }
 * @param {Object} context - { clientName, dateRange, dimensions, metrics, fieldsData }
 * @param {Object} tableData - { headers, rows, summary, isComparison }
 */
export async function exportReportToPDF(refs, context = {}, tableData = null) {
    const { lineChartRef, chartsSectionRef } = refs;

    const chartSections = [
        chartsSectionRef,
        lineChartRef,
    ].filter(ref => ref && ref.current);

    const hasCharts = chartSections.length > 0;
    const hasTable = tableData && tableData.headers && tableData.headers.length > 0;

    if (!hasCharts && !hasTable) return;

    const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;

    // Header naranja
    pdf.setFillColor(255, 71, 15); // #FF470F
    pdf.rect(0, 0, pageWidth, 18, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Reporte \u2014 Explorador de Datos', margin, 11);

    const { clientName, dateRange } = context;
    if (clientName || dateRange) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(255, 220, 200);
        const subtitle = [clientName, dateRange].filter(Boolean).join('  \u00b7  ');
        pdf.text(subtitle, pageWidth - margin, 11, { align: 'right' });
    }

    let currentY = 22;

    // --- Charts como JPEG ---
    for (let i = 0; i < chartSections.length; i++) {
        const ref = chartSections[i];
        if (!ref || !ref.current) continue;

        let canvas;
        try {
            canvas = await html2canvas(ref.current, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                scale: 1.5,
                logging: false,
            });
        } catch (e) {
            continue;
        }

        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const availableHeight = pageHeight - margin;

        const imgData = canvas.toDataURL('image/jpeg', 0.85);

        if (currentY + imgHeight <= availableHeight) {
            // Cabe en el espacio restante de la página actual
            pdf.addImage(imgData, 'JPEG', margin, currentY, imgWidth, imgHeight);
            currentY += imgHeight + 6;
        } else {
            // No cabe: mover a página nueva (nunca cortar un gráfico)
            const fullPageContentHeight = pageHeight - margin * 2;
            pdf.addPage();
            currentY = margin;

            if (imgHeight <= fullPageContentHeight) {
                // Cabe en una página completa
                pdf.addImage(imgData, 'JPEG', margin, currentY, imgWidth, imgHeight);
                currentY += imgHeight + 6;
            } else {
                // Más alto que una página: escalar para que entre
                const scaleFactor = fullPageContentHeight / imgHeight;
                const scaledWidth = imgWidth * scaleFactor;
                const scaledHeight = fullPageContentHeight;
                const xOffset = margin + (contentWidth - scaledWidth) / 2;
                pdf.addImage(imgData, 'JPEG', xOffset, currentY, scaledWidth, scaledHeight);
                currentY += scaledHeight + 6;
            }
        }
    }

    // --- Tabla como texto nativo ---
    if (hasTable) {
        const { headers, rows, summary, isComparison } = tableData;

        // Preparar body: summary row primero, luego data rows
        const bodyRows = [];
        const summaryRow = buildSummaryRow(headers, summary, isComparison);
        const hasSummary = summaryRow !== null;

        if (hasSummary) {
            bodyRows.push(summaryRow);
        }

        for (const row of rows) {
            const formattedRow = {};
            for (const header of headers) {
                formattedRow[header.key] = getFormattedCellValue(row[header.key], isComparison);
            }
            bodyRows.push(formattedRow);
        }

        // Determinar fontSize según cantidad de columnas
        const baseFontSize = headers.length > 12 ? 6 : 7;
        const headerFontSize = headers.length > 12 ? 6.5 : 7.5;

        // Si no hay espacio suficiente para al menos el header de la tabla, nueva página
        if (currentY > pageHeight - 30) {
            pdf.addPage();
            currentY = margin;
        }

        pdf.autoTable({
            startY: currentY,
            columns: headers.map(h => ({ header: h.label, dataKey: h.key })),
            body: bodyRows,
            theme: 'grid',
            margin: { left: margin, right: margin },
            styles: {
                font: 'helvetica',
                fontSize: baseFontSize,
                cellPadding: isComparison ? 3 : 2,
                overflow: 'linebreak',
                lineColor: [220, 220, 220],
                lineWidth: 0.2,
            },
            headStyles: {
                fillColor: [255, 71, 15],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: headerFontSize,
                halign: 'center',
            },
            alternateRowStyles: {
                fillColor: [249, 249, 249],
            },
            columnStyles: buildColumnStyles(headers),
            didParseCell: (data) => {
                const raw = data.cell.raw;

                // Manejar comparison cells: extraer mainLine como texto
                if (typeof raw === 'object' && raw !== null && raw.mainLine !== undefined) {
                    data.cell.text = [raw.mainLine];
                }

                // Estilizar summary row (index 0 si existe)
                if (hasSummary && data.section === 'body' && data.row.index === 0) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            },
            didDrawCell: (data) => {
                if (data.section !== 'body') return;

                const raw = data.cell.raw;
                if (typeof raw !== 'object' || !raw || !raw.deltaLine) return;

                // Dibujar deltaLine en gris pequeño debajo del valor principal
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(5.5);
                pdf.setTextColor(130, 130, 130);

                const cellX = data.cell.x;
                const cellWidth = data.cell.width;
                const cellY = data.cell.y;
                const cellHeight = data.cell.height;

                // Alinear a la derecha para métricas, izquierda para dimensiones
                const isMetric = headers[data.column.index]?.key?.startsWith('metric_');
                const textX = isMetric
                    ? cellX + cellWidth - 2
                    : cellX + 2;
                const textY = cellY + cellHeight - 1.5;
                const align = isMetric ? 'right' : 'left';

                pdf.text(raw.deltaLine, textX, textY, { align });
            },
        });
    }

    const today = new Date().toISOString().split('T')[0];
    pdf.save(`reporte-explorador-${today}.pdf`);
}
